import Cookies from 'js-cookie';
import {
  loadPipelineActions,
  loadSearchAnalyticsActions,
  loadSearchActions,
  loadSortCriteriaActions,
  loadAdvancedSearchQueryActions,
  buildFieldSortCriterion
} from '@coveo/headless';

const locationDataStorage = 'ssl-location-data'
//hijacking this method and moving data to local storage
const getLocationCookie = locationCookieName => {
  const locationCookie = localStorage.getItem(locationDataStorage);
  return locationCookie ? JSON.parse(locationCookie) : null;
};

const setLocationCookie = (args) => {
  Object.keys(args).forEach((key) => {
    if (args[key] == 'null') {
      args[key] = null;
    }
  });

  const prevCookieData = getLocationCookie() || {};

  Object.keys(prevCookieData).forEach((key) => {
    if (prevCookieData[key] == 'null') {
      prevCookieData[key] = null;
    }
  });

  const cookieData = {
    ...prevCookieData,
    ...args
  };

  localStorage.setItem(locationDataStorage, JSON.stringify(cookieData));
}

const unitConversion = (distance, unit) => {
  const miles = 0.000621371;
  const kilometers = 0.001;

  if (unit === 'mi') {
    return (distance * miles).toFixed(2);
  }
  return (distance * kilometers).toFixed(2);
};

const getGoogleMapsInfoWindowContentString = ({
  linkToAnchor = null,
  linkWholeCard = true,
  result,
  showDistance = true,
  imageDomain = '',
  unit = 'mi',
  unitLabelCard = 'mi',
  underlineTitle = false
}) => {
  const imgString =
    result.raw.ssl_thumbnailimage || result.raw.ssl_mainimagedesktop
      ? `<img src="${imageDomain}${
          result.raw.ssl_thumbnailimage
            ? result.raw.ssl_thumbnailimage
            : result.raw.ssl_mainimagedesktop
        }">`
      : '';
  const titleString = result.raw.ssl_communityname
    ? `<h4 class="map-info-window__header map-info-window__title">${
        result.raw.ssl_communityname
          ? result.raw.ssl_communityname
          : result.raw.title
      }</h4>`
    : '';
  const contentString = `${
    linkWholeCard
      ? `<a href='${
          linkToAnchor ? linkToAnchor : result.raw.uri
        }' class="map-info-window ${
          underlineTitle ? 'map-info-window__underline-title' : ''
        }"'>`
      : ''
  }<div class="map-info-window__info-window">
  <div class="map-info-window__image-container">
        ${imgString}
        </div>
        <div class="map-info-window__text">
        ${titleString} ${
    showDistance
      ? `<div class="map-info-window__distance">${result.raw.distance_search} ${unitLabelCard}</div>`
      : ''
  }
        </div>
        </div> ${linkWholeCard ? '</a>' : ''}`;

  return contentString;
};

const setCurrentCommunityCookie = result => {
  const {
    module,
    distance,
    ssl_latitude_d,
    ssl_longitude_d,
    ssl_communityname,
    ssl_thumbnailimage,
    ssl_pricingandavailabilityphone,
    ssl_residentsandfamilyphone,
    ssl_communityid,
    uri,
    ssl_mainimagedesktop
  } = result.raw;
  const currentCommunityData = {
    ssl_latitude: ssl_latitude_d,
    ssl_longitude: ssl_longitude_d,
    ssl_communityname,
    ssl_thumbnailimage: ssl_thumbnailimage
      ? ssl_thumbnailimage
      : ssl_mainimagedesktop,
    ssl_pricingandavailabilityphone,
    ssl_residentsandfamilyphone,
    ssl_communityid,
    ssl_communitylink: uri,
    ssl_distance: distance
  };
  const currentCommunityString = JSON.stringify(currentCommunityData);
  Cookies.set('current_community', currentCommunityString);
};

const filterProtocol = uri => {
  const isAbsolute = /^(https?|ftp|file|mailto|tel|sip):/i.test(uri);
  const isRelative = /^(\/|\.\/|\.\.\/)/.test(uri);

  return isAbsolute || isRelative ? uri : '';
};

const getQueryFunctionsLatitudeLongitude = (latitude, longitude) => {
  return [
    {
      fieldName: `@distance`,
      function: `dist(@ssl_latitude_d,@ssl_longitude_d, ${parseFloat(
        latitude
      )}, ${parseFloat(longitude)})`
    },
    {
      fieldName: `@distanceInMiles`,
      function: `@distance*0.000621371`
    }
  ];
};

const setLatitudeLongitudePreprocessRequest = (latitude, longitude) => {
  const global = window;
  global.preProcessRequest = body => {
    const newBody = JSON.parse(body.toString());
    newBody.queryFunctions = getQueryFunctionsLatitudeLongitude(
      latitude,
      longitude
    );
    return JSON.stringify(newBody);
  };
};

const getAdvancedGeolocationQuery = ({
  source,
  radiusInMiles = 50,
  careType = null,
  state = null,
  contextLanguage = 'en',
  hiddenCommunitiesArray = []
}) => {
  return {
    aq: `(@source=="${source}") ${
      state === null && radiusInMiles
        ? `(@distanceInMiles<=${radiusInMiles})`
        : ''
    } ${
      careType ? `(@ssl_levelsofcare=="${careType}")` : ''
    } (@ssl_contextlanguage==${contextLanguage}) ${
      state ? `(@ssl_stateabbreviation=="${state}")` : ''
    } (@ssl_pagetype=='community_pagetype') ${
      hiddenCommunitiesArray.length > 0
        ? hiddenCommunitiesArray
            .map(el => `(NOT @ssl_communityid=="${el}")`)
            .join(' ')
        : ''
    }`
  };
};

const getCoveoFindACommunityFieldsToInclude = () => {
  return [
    'distance',
    'uri',
    'ssl_latitude_d',
    'ssl_longitude_d',
    'ssl_thumbnailimage',
    'ssl_mainimagedesktop',
    'ssl_communityid',
    'ssl_communityname',
    'ssl_heading',
    'ssl_contextlanguage',
    'ssl_state',
    'ssl_city',
    'ssl_zipcode',
    'ssl_stateabbreviation',
    'ssl_levelsofcare',
    'ssl_pricingandavailabilityphone',
    'ssl_residentsandfamilyphone',
    'ssl_communityprice',
    'ssl_commnunitystatus',
    'ssl_mainimagedesktop_405x548'
  ];
};

const sendCoveoInterfaceLoad = ({ engine, pipeline = 'default' }) => {
  const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);
  const { executeSearch } = loadSearchActions(engine);
  engine.dispatch(executeSearch(logInterfaceLoad()));
};

const sendCoveoGeolocationInterfaceLoad = ({
  engine,
  latitude,
  longitude,
  source,
  advancedUrlQuery = null,
  radiusInMiles,
  state = null,
  language,
  pipeline = 'default',
  hiddenCommunitiesArray = [],
  previousLoad = false
}) => {
  if (previousLoad) {
    return;
  }

  const { registerSortCriterion } = loadSortCriteriaActions(engine);
  const { setPipeline } = loadPipelineActions(engine);
  const { logInterfaceLoad, logSearchFromLink } =
    loadSearchAnalyticsActions(engine);
  const { executeSearch } = loadSearchActions(engine);
  setLatitudeLongitudePreprocessRequest(latitude, longitude);
  const advancedQuerySearchActionCreator =
    loadAdvancedSearchQueryActions(engine);
  let advancedQuery;
  if (advancedUrlQuery) {
    advancedQuery = {
      aq: `(@source=="${source}") (@ssl_contextlanguage==${language}) (@ssl_pagetype=='community_pagetype') ${advancedUrlQuery}`
    };
  } else {
    advancedQuery = getAdvancedGeolocationQuery({
      source: source,
      state: state,
      radiusInMiles: radiusInMiles ? parseFloat(radiusInMiles) : null,
      contextLanguage: language,
      hiddenCommunitiesArray: hiddenCommunitiesArray
    });
  }

  engine.dispatch(
    registerSortCriterion(buildFieldSortCriterion('distance', 'ascending'))
  );
  engine.dispatch(setPipeline(pipeline));
  engine.dispatch(
    advancedQuerySearchActionCreator.registerAdvancedSearchQueries(
      advancedQuery
    )
  );
  if (advancedUrlQuery) engine.dispatch(executeSearch(logSearchFromLink()));
  else engine.dispatch(executeSearch(logInterfaceLoad()));
};

const sendCoveoGeolocationQuery = ({
  engine,
  latitude,
  longitude,
  advancedUrlQuery,
  source = 'Production US - English',
  radiusInMiles = 50,
  careType = null,
  state = null,
  language = 'en',
  pipeline = 'default',
  hiddenCommunitiesArray = []
}) => {
  const { registerSortCriterion } = loadSortCriteriaActions(engine);
  const { setPipeline } = loadPipelineActions(engine);
  const { logSearchboxSubmit } = loadSearchAnalyticsActions(engine);
  const { executeSearch } = loadSearchActions(engine);
  setLatitudeLongitudePreprocessRequest(latitude, longitude);
  const advancedQuerySearchActionCreator =
    loadAdvancedSearchQueryActions(engine);
  let advancedQuery;
  if (advancedUrlQuery) {
    advancedQuery = {
      hq: `(@source=="${source}") (@ssl_contextlanguage==${language}) (@ssl_pagetype=='community_pagetype') ${advancedUrlQuery}`
    };
  } else {
    advancedQuery = getAdvancedGeolocationQuery({
      source: source,
      state: state,
      careType: careType,
      radiusInMiles: radiusInMiles ? parseFloat(radiusInMiles) : null,
      contextLanguage: language,
      hiddenCommunitiesArray: hiddenCommunitiesArray
    });
  }
  engine.dispatch(
    registerSortCriterion(buildFieldSortCriterion('distance', 'ascending'))
  );
  engine.dispatch(setPipeline(pipeline));
  engine.dispatch(
    advancedQuerySearchActionCreator.registerAdvancedSearchQueries(
      advancedQuery
    )
  );
  engine.dispatch(executeSearch(logSearchboxSubmit()));
};

const checkForAdvancedQueryInHash = () => {
  const hash = window.location.hash.slice(1);
  const params = new URLSearchParams(hash);
  const advancedQuery = params.get('aq');
  if (advancedQuery) return advancedQuery;
  return null;
};

const checkForHiddenQueryInHash = () => {
  const hash = window.location.hash.slice(1);
  const params = new URLSearchParams(hash);
  const hiddenQuery = params.get('hq');
  if (hiddenQuery) return hiddenQuery;
  return null;
};

const clearAllFacets = () => {
  const hash = window.location.hash;
  const hashArray = hash.split('&');
  const newHashArray = hashArray.filter(item => !item.includes('f'));
  const newHash = newHashArray.join('&');
  window.location.hash = newHash;
};

const getModuleStorageCacheData = module => {
  const localStorageData = localStorage.getItem('ssl-modules-cache');
  const localStorageDataParsed = localStorageData
    ? JSON.parse(localStorageData)
    : null;
  if (module) {
    return localStorageDataParsed ? localStorageDataParsed[module] : null;
  }
  return localStorageDataParsed;
};

const setModuleStorageCacheData = (module, value, locationInfo) => {
  const prevLocaStorageData = getModuleStorageCacheData() || {};
  const localStorageData = {
    ...prevLocaStorageData,
    [module]: {
      value: value,
      locationInfo: locationInfo
    }
  };
  localStorage.setItem('ssl-modules-cache', JSON.stringify(localStorageData));
};

const compareLocationInfo = (locationInfo1, locationInfo2) => {
  if (!locationInfo1 || !locationInfo2) return false;
  if (
    locationInfo1.Latitude === locationInfo2.Latitude &&
    locationInfo1.Longitude === locationInfo2.Longitude
  ) {
    return true;
  }
  return false;
};

const sortResultsByDistance = results => {
  //sort array objects by distance
  if (!results) return [];
  const sortedResults = results.sort((a, b) => {
    // console.log(a.raw.distance, b.raw.distance);
    if (a.raw.distance === b.raw.distance) return 0;
    if (a.raw.distance > b.raw.distance) return 1;
    if (a.raw.distance < b.raw.distance) return -1;
  });
  return sortedResults;
};

export {
  sortResultsByDistance,
  clearAllFacets,
  checkForHiddenQueryInHash,
  checkForAdvancedQueryInHash,
  sendCoveoInterfaceLoad,
  sendCoveoGeolocationQuery,
  sendCoveoGeolocationInterfaceLoad,
  setLatitudeLongitudePreprocessRequest,
  getCoveoFindACommunityFieldsToInclude,
  getAdvancedGeolocationQuery,
  getQueryFunctionsLatitudeLongitude,
  getLocationCookie,
  setLocationCookie,
  filterProtocol,
  getGoogleMapsInfoWindowContentString,
  unitConversion,
  setCurrentCommunityCookie,
  setModuleStorageCacheData,
  getModuleStorageCacheData,
  compareLocationInfo
};
